import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Globe, Wifi, WifiOff, Ban } from 'lucide-react';
import type { Shop } from '@/lib/types';

import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { getShopLogoUrl } from '@/lib/utils/image';

interface ShopCardProps {
  shop: Shop;
}

function getStatusConfig(status?: string, isActive?: boolean) {
  if (status === 'suspended') {
    return {
      variant: 'destructive' as const,
      icon: Ban,
      labelKey: 'shops.suspended',
    };
  }
  if (status === 'offline' || !isActive) {
    return {
      variant: 'secondary' as const,
      icon: WifiOff,
      labelKey: 'shops.offline',
    };
  }
  return {
    variant: 'success' as const,
    icon: Wifi,
    labelKey: 'shops.online',
  };
}

export function ShopCard({ shop }: ShopCardProps) {
  const { t } = useTranslation();
  const statusConfig = getStatusConfig(shop.status, shop.isActive);
  const StatusIcon = statusConfig.icon;

  // Get country name from string or object
  const countryName = typeof shop.country === 'object'
    ? shop.country?.name
    : shop.country;

  return (
    <Link to={`/shops/${shop.refId || shop.id}`}>
      <Card className="overflow-hidden hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group">
        {/* Header with gradient background */}
        <div className="h-28 relative bg-gradient-to-br from-primary/10 via-primary/5 to-background">
          {/* Logo centered */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Avatar className="h-16 w-16 border-4 border-background shadow-lg group-hover:scale-105 transition-transform">
              <AvatarImage src={getShopLogoUrl(shop)} />
              <AvatarFallback className="text-xl font-bold bg-primary/10">
                {shop.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Status badge top right */}
          <div className="absolute top-2 right-2">
            <Badge variant={statusConfig.variant} className="gap-1 text-xs">
              <StatusIcon className="size-3" />
              {t(statusConfig.labelKey)}
            </Badge>
          </div>

          {/* Promotion badge top left */}
          {shop.hasActivePromotions && (
            <div className="absolute top-2 left-2">
              <Badge variant="default" className="gap-1 text-xs bg-orange-500 hover:bg-orange-600">
                <Tag className="size-3" />
                {t('shops.hasPromotions', { defaultValue: 'Promos' })}
              </Badge>
            </div>
          )}
        </div>

        <CardContent className="pt-4 pb-4">
          {/* Shop name */}
          <h3 className="font-semibold text-base truncate text-center mb-2 group-hover:text-primary transition-colors">
            {shop.name}
          </h3>

          {/* Location */}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            {shop.city && (
              <span className="flex items-center gap-1">
                <MapPin className="size-3 shrink-0" />
                <span className="truncate">{shop.city}</span>
              </span>
            )}
            {shop.city && countryName && (
              <span className="text-muted-foreground/50">•</span>
            )}
            {countryName && (
              <span className="flex items-center gap-1">
                <Globe className="size-3 shrink-0" />
                <span className="truncate">{countryName}</span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
